/** Ключи корневых структур в Y.Doc доски. */
export const Y_KEYS = {
  objects: 'objects',
  meta: 'meta',
  comments: 'comments',
  /** Служебное состояние сессий: голосование, таймер, презентация. */
  session: 'session',
} as const;

/** Ключ Y.XmlFragment с rich text внутри Y.Map объекта. */
export const OBJECT_TEXT_KEY = 'text';
/** Ключ вложенной Y.Map с пропсами объекта. */
export const OBJECT_PROPS_KEY = 'props';

export const OBJECT_TYPES = [
  'sticky',
  'shape',
  'text',
  'pen',
  'image',
  'connector',
  'frame',
  'card',
  'table',
  'mindmap-node',
  'embed',
  'stamp',
  'vote-dot',
  'group',
] as const;
export type ObjectType = (typeof OBJECT_TYPES)[number];

/** Типы, у которых есть rich text в Y.XmlFragment. */
export const RICH_TEXT_TYPES: ReadonlySet<ObjectType> = new Set<ObjectType>([
  'sticky',
  'shape',
  'text',
  'card',
  'mindmap-node',
]);

export const SHAPE_KINDS = [
  'rect',
  'rounded',
  'ellipse',
  'diamond',
  'triangle',
  'star',
  'hexagon',
  'arrow',
  'cloud',
  'cylinder',
  'parallelogram',
] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

export const BOARD_FONTS = {
  inter: { label: 'Inter', css: '"Inter Variable", "Inter", system-ui, sans-serif' },
  caveat: { label: 'Caveat', css: '"Caveat", cursive' },
  mono: { label: 'Geist Mono', css: '"Geist Mono Variable", "Geist Mono", ui-monospace, monospace' },
  lora: { label: 'Lora', css: '"Lora Variable", "Lora", Georgia, serif' },
} as const;
export type BoardFont = keyof typeof BOARD_FONTS;
export const BOARD_FONT_KEYS = Object.keys(BOARD_FONTS) as BoardFont[];

export const STROKE_STYLES = ['solid', 'dashed', 'dotted'] as const;
export type StrokeStyle = (typeof STROKE_STYLES)[number];

export const TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type TextAlign = (typeof TEXT_ALIGNS)[number];
export const VERTICAL_ALIGNS = ['top', 'middle', 'bottom'] as const;
export type VerticalAlign = (typeof VERTICAL_ALIGNS)[number];

export const ARROW_HEADS = ['none', 'arrow', 'triangle', 'circle', 'diamond', 'bar'] as const;
export type ArrowHead = (typeof ARROW_HEADS)[number];

export const LINE_TYPES = ['straight', 'elbow', 'curved'] as const;
export type LineType = (typeof LINE_TYPES)[number];

export const ANCHORS = ['auto', 'top', 'right', 'bottom', 'left', 'center'] as const;
export type Anchor = (typeof ANCHORS)[number];

export const EMBED_PROVIDERS = ['youtube', 'vimeo', 'figma', 'gdocs', 'link'] as const;
export type EmbedProvider = (typeof EMBED_PROVIDERS)[number];

export const STICKY_SIZES = { S: 160, M: 220, L: 320 } as const;
export type StickySize = keyof typeof STICKY_SIZES;

export const GRID_MODES = ['dots', 'lines', 'none'] as const;
export type GridMode = (typeof GRID_MODES)[number];

// ─── Доступ ──────────────────────────────────────────────────────────

export const WORKSPACE_ROLES = ['owner', 'admin', 'member', 'guest'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export const BOARD_ROLES = ['owner', 'editor', 'commenter', 'viewer'] as const;
export type BoardRole = (typeof BOARD_ROLES)[number];

const BOARD_ROLE_RANK: Record<BoardRole, number> = { viewer: 1, commenter: 2, editor: 3, owner: 4 };
export function roleAtLeast(role: BoardRole | null | undefined, min: BoardRole): boolean {
  return !!role && BOARD_ROLE_RANK[role] >= BOARD_ROLE_RANK[min];
}
export function maxRole(a: BoardRole | null, b: BoardRole | null): BoardRole | null {
  if (!a) return b;
  if (!b) return a;
  return BOARD_ROLE_RANK[a] >= BOARD_ROLE_RANK[b] ? a : b;
}

const WORKSPACE_ROLE_RANK: Record<WorkspaceRole, number> = { guest: 1, member: 2, admin: 3, owner: 4 };
export function workspaceRoleAtLeast(role: WorkspaceRole | null | undefined, min: WorkspaceRole): boolean {
  return !!role && WORKSPACE_ROLE_RANK[role] >= WORKSPACE_ROLE_RANK[min];
}

export const BOARD_VISIBILITY = ['private', 'workspace', 'link', 'public'] as const;
export type BoardVisibility = (typeof BOARD_VISIBILITY)[number];

// ─── Публичный API ───────────────────────────────────────────────────

export const API_TOKEN_PREFIX = 'lmo_';

export const API_SCOPES = [
  'boards:read',
  'boards:write',
  'comments:read',
  'comments:write',
  'webhooks:manage',
  'workspace:read',
] as const;
export type ApiScope = (typeof API_SCOPES)[number];

export const WEBHOOK_EVENTS = [
  'board.created',
  'board.updated',
  'board.deleted',
  'object.created',
  'object.updated',
  'object.deleted',
  'comment.created',
  'comment.resolved',
  'member.added',
  'member.removed',
  'vote.finished',
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

export const WEBHOOK_MAX_ATTEMPTS = 8;
/** После стольких подряд неудачных доставок вебхук отключается. */
export const WEBHOOK_DISABLE_AFTER_FAILURES = 20;

// ─── Лимиты по умолчанию ─────────────────────────────────────────────

export const LIMITS = {
  maxObjectsPerBoard: 50_000,
  maxUpdateBytes: 2_000_000,
  maxBatchCreate: 500,
  maxTextLength: 20_000,
  maxPenPoints: 20_000,
  minZoom: 0.02,
  maxZoom: 8,
  trashDays: 30,
} as const;

export const CURSOR_SEND_INTERVAL_MS = 40;
export const LASER_FADE_MS = 1500;
